import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useRealtimeAlbums } from '@/features/albums/hooks/useRealtimeAlbums';
import { useAlbumStore } from '@/features/albums/stores/albumStore';
import { useRealtimeEvents } from '@/features/family-calendar/hooks/useRealtimeEvents';
import { useCalendarStore } from '@/features/family-calendar/stores/calendarStore';
import { usePresence } from '@/features/p2p-chat/hooks/usePresence';
import { useSupabaseFamilyPresence } from '@/features/p2p-chat/hooks/useSupabaseFamilyPresence';
import { useRealtimeTasks } from '@/features/todo-list/hooks/useRealtimeTasks';
import { useTodoStore } from '@/features/todo-list/stores/todoStore';
import { MobileSidebar } from '@/shared/components/layouts/MobileSidebar';
import { Sidebar } from '@/shared/components/layouts/Sidebar';
import { useBrowserNotifications } from '@/shared/hooks/useBrowserNotifications';
import { useRefetchOnFocus } from '@/shared/hooks/useRefetchOnFocus';
import { useNotificationStore } from '@/shared/stores/notificationStore';
import { Header } from './Header';

export function MainLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Open the global presence connection and mirror new in-app notifications to
  // the OS while authenticated. Both no-op when prerequisites aren't met.
  // usePresence = worker socket (chat invite relay ONLY — transport);
  // useSupabaseFamilyPresence = Supabase 3-state status source (all UI dots).
  usePresence();
  useSupabaseFamilyPresence();
  useBrowserNotifications();

  // Family-data realtime subscriptions live at the layout level so cross-user
  // changes stream in for the whole session, not just while a feature page is
  // mounted (notifications are subscribed in the Header).
  useRealtimeTasks();
  useRealtimeEvents();
  useRealtimeAlbums();

  // Catch-up safety net: refetch all family data when the tab regains focus or
  // the browser comes back online (realtime may have silently missed changes).
  const refetchTasks = useTodoStore((s) => s.refetch);
  const refetchCalendar = useCalendarStore((s) => s.refetch);
  const refetchAlbums = useAlbumStore((s) => s.refetch);
  const refetchNotifications = useNotificationStore((s) => s.refetch);
  useRefetchOnFocus(() => {
    void refetchTasks();
    void refetchCalendar();
    void refetchAlbums();
    void refetchNotifications();
  });

  return (
    <div className="flex min-h-screen flex-col">
      <Header onMenuClick={() => setIsMobileMenuOpen(true)} />
      <div className="flex flex-1">
        <Sidebar />
        <MobileSidebar
          open={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
        <main className="flex-1 overflow-y-auto bg-background p-4 sm:p-6">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
